import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Upload, FileSpreadsheet, Trash2, Download, RefreshCw, CheckCircle2,
  AlertTriangle, TrendingDown, X, Search,
} from "lucide-react";

const CHUNK = 700 * 1024; // ~700KB base64 parçaları

const fmtTL = (v) => `₺${Number(v || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// ══════════════ ALIŞ-SATIŞ MARJ RAPORU ══════════════
export function MarginReport({ api, threshold = 20 }) {
  const [rows, setRows] = useState([]);
  const [rates, setRates] = useState({ eurToTl: 38, usdToTl: 34 });
  const [q, setQ] = useState("");
  const [brand, setBrand] = useState("");
  const [onlyLow, setOnlyLow] = useState(false);
  const [loading, setLoading] = useState(true);
  const [sel, setSel] = useState({});
  const [targetMargin, setTargetMargin] = useState(50);
  const [applying, setApplying] = useState(false);

  const load = async () => {
    const [a, r] = await Promise.all([api.get("/assets?kind=fabric"), api.get("/rates")]);
    setRows(a.data); setRates(r.data); setLoading(false);
  };
  useEffect(() => { load(); }, [api]); // eslint-disable-line

  const toTL = (v, cur) => (cur === "EUR" ? v * rates.eurToTl : cur === "USD" ? v * rates.usdToTl : v);

  const data = useMemo(() => {
    const groups = {};
    rows.forEach((a) => {
      const key = `${a.label}|${a.group}|${a.purchasePrice}|${a.price}|${a.outOfStock ? 1 : 0}`;
      if (!groups[key]) groups[key] = { ...a, codes: [], ids: [] };
      groups[key].codes.push(a.code);
      groups[key].ids.push(a.id);
    });
    return Object.values(groups).map((g) => {
      const costTL = toTL(g.purchasePrice || 0, g.purchaseCurrency || "TL");
      const saleTL = toTL(g.price || 0, g.currency || "EUR");
      const pct = saleTL > 0 && costTL > 0 ? ((saleTL - costTL) / saleTL) * 100 : null;
      const cs = g.codes.sort();
      return { ...g, codeRange: cs.length > 1 ? `${cs[0]} – ${cs[cs.length - 1]}` : cs[0], codeCount: cs.length, costTL, saleTL, pct };
    }).sort((a, b) => {
      if (a.pct === null) return 1;
      if (b.pct === null) return -1;
      return a.pct - b.pct;
    });
  }, [rows, rates]);

  const brands = useMemo(() => [...new Set(rows.map((r) => r.brand).filter(Boolean))].sort(), [rows]);
  const view = data.filter((d) => {
    if (brand && d.brand !== brand) return false;
    if (onlyLow && !(d.pct !== null && d.pct < threshold)) return false;
    if (q) {
      const s = `${d.label} ${d.codeRange} ${d.group}`.toLowerCase();
      if (!s.includes(q.toLowerCase())) return false;
    }
    return true;
  });
  const withMargin = data.filter((d) => d.pct !== null);
  const avg = withMargin.length ? withMargin.reduce((s, d) => s + d.pct, 0) / withMargin.length : 0;
  const lowCount = withMargin.filter((d) => d.pct < threshold).length;
  const noCost = data.filter((d) => !d.purchasePrice).length;

  const rowKey = (d) => `${d.label}-${d.codeRange}-${d.group}`;
  const selCount = Object.values(sel).filter(Boolean).length;
  const toggleAll = (checked) => setSel(checked ? Object.fromEntries(view.filter((d) => d.purchasePrice).map((d) => [rowKey(d), true])) : {});

  const applyBulkMargin = async () => {
    const ids = view.filter((d) => sel[rowKey(d)]).flatMap((d) => d.ids);
    if (!ids.length) return toast.error("Hiç kumaş seçilmedi");
    if (!window.confirm(`${ids.length} kumaş kodu için satış fiyatı %${targetMargin} marjla yeniden hesaplansın mı?`)) return;
    setApplying(true);
    try {
      const { data: r } = await api.post("/assets/bulk-margin", { ids, marginPercent: Number(targetMargin) });
      toast.success(`${r.updated} kumaş güncellendi${r.skipped ? ` · ${r.skipped} atlandı (alış fiyatı yok)` : ""}`);
      setSel({});
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Güncelleme başarısız"); } finally { setApplying(false); }
  };

  if (loading) return <div className="text-xs text-slate-500 p-4">Yükleniyor…</div>;

  return (
    <div className="space-y-4" data-testid="margin-report">
      <div className="grid md:grid-cols-4 gap-3">
        <div className="zen-panel p-4"><div className="text-[10px] font-bold text-slate-500 uppercase">Ortalama Marj</div>
          <div className="text-2xl font-extrabold mt-1 text-emerald-500 zen-mono" data-testid="margin-avg">%{avg.toFixed(1)}</div></div>
        <div className="zen-panel p-4"><div className="text-[10px] font-bold text-slate-500 uppercase">%{threshold} Altı Kumaş</div>
          <div className="text-2xl font-extrabold mt-1 text-rose-500 zen-mono" data-testid="margin-low-count">{lowCount}</div></div>
        <div className="zen-panel p-4"><div className="text-[10px] font-bold text-slate-500 uppercase">Alış Fiyatı Yok</div>
          <div className="text-2xl font-extrabold mt-1 text-amber-500 zen-mono">{noCost}</div></div>
        <div className="zen-panel p-4"><div className="text-[10px] font-bold text-slate-500 uppercase">Kur (1 EUR)</div>
          <div className="text-2xl font-extrabold mt-1 zen-mono">{fmtTL(rates.eurToTl)}</div></div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-52">
          <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input className="zen-input !pl-8 !py-2" placeholder="Kumaş adı veya kod ara…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="margin-search" />
        </div>
        <select className="zen-input !py-2 !w-auto" value={brand} onChange={(e) => setBrand(e.target.value)} data-testid="margin-brand-filter">
          <option value="">Tüm markalar</option>
          {brands.map((b) => <option key={b} value={b}>{b}</option>)}
        </select>
        <button onClick={() => setOnlyLow((v) => !v)} data-testid="margin-low-filter"
          className={`px-3 py-2 rounded-full text-[11px] font-bold border transition-all ${onlyLow ? "bg-rose-500 text-white border-rose-500" : "border-rose-500/40 text-rose-500 hover:bg-rose-500/10"}`}>
          <TrendingDown className="w-3.5 h-3.5 inline mr-1" />Zayıf Marjlar (%{threshold} altı)
        </button>
      </div>

      <div className="zen-panel p-3 flex flex-wrap items-center gap-2" data-testid="margin-bulk-bar">
        <span className="text-[11px] font-bold text-slate-500">Toplu Marj Güncelleme:</span>
        <span className="text-[11px] text-slate-400">{selCount} kumaş seçili</span>
        <div className="flex items-center gap-1">
          <input type="number" min={0} max={99} className="zen-input !py-1.5 !w-20 !text-xs" value={targetMargin}
            onChange={(e) => setTargetMargin(e.target.value)} data-testid="margin-bulk-input" />
          <span className="text-xs font-bold">%</span>
        </div>
        <button onClick={applyBulkMargin} disabled={applying || selCount === 0} className="zen-btn-primary !py-1.5 !px-3 text-[11px] disabled:opacity-40" data-testid="margin-bulk-apply-btn">
          {applying ? "Uygulanıyor…" : `Seçilenlere %${targetMargin} Marj Uygula (${selCount})`}
        </button>
        <span className="text-[10px] text-slate-400 ml-auto">Yeni Satış = Alış / (1 − marj/100)</span>
      </div>

      <div className="zen-panel overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-slate-50 dark:bg-[#131B2E] text-slate-500">
            <tr className="text-left">
              <th className="px-3 py-2">
                <input type="checkbox" data-testid="margin-select-all"
                  checked={view.length > 0 && view.filter((d) => d.purchasePrice).length > 0 && selCount === view.filter((d) => d.purchasePrice).length}
                  onChange={(e) => toggleAll(e.target.checked)} />
              </th>
              <th className="px-3 py-2 font-bold">Kumaş</th>
              <th className="px-3 py-2 font-bold">Kod</th>
              <th className="px-3 py-2 font-bold">Marka</th>
              <th className="px-3 py-2 font-bold text-right">Alış</th>
              <th className="px-3 py-2 font-bold text-right">Satış</th>
              <th className="px-3 py-2 font-bold text-right">Kâr / m²</th>
              <th className="px-3 py-2 font-bold text-right">Marj</th>
              <th className="px-3 py-2 font-bold">Durum</th>
            </tr>
          </thead>
          <tbody>
            {view.map((d) => (
              <tr key={rowKey(d)} data-testid={`margin-row-${d.codes[0]}`}
                className={`border-t border-slate-100 dark:border-[#22304A] ${d.pct !== null && d.pct < threshold ? "bg-rose-500/10" : ""}`}>
                <td className="px-3 py-2">
                  <input type="checkbox" disabled={!d.purchasePrice} checked={!!sel[rowKey(d)]}
                    onChange={(e) => setSel({ ...sel, [rowKey(d)]: e.target.checked })}
                    data-testid={`margin-select-${d.codes[0]}`} />
                </td>
                <td className="px-3 py-2 font-semibold">{d.label}<div className="text-[10px] text-slate-500">{d.group}</div></td>
                <td className="px-3 py-2 zen-mono">{d.codeRange}{d.codeCount > 1 && <span className="text-slate-400"> ({d.codeCount})</span>}</td>
                <td className="px-3 py-2 text-slate-500">{d.brand || "—"}</td>
                <td className="px-3 py-2 text-right zen-mono">{d.purchasePrice ? fmtTL(d.costTL) : <span className="text-amber-500">yok</span>}</td>
                <td className="px-3 py-2 text-right zen-mono">{d.price ? `${d.price} ${d.currency}` : <span className="text-amber-500">yok</span>}<div className="text-[10px] text-slate-400">{d.price ? fmtTL(d.saleTL) : ""}</div></td>
                <td className="px-3 py-2 text-right zen-mono">{d.pct !== null ? fmtTL(d.saleTL - d.costTL) : "—"}</td>
                <td className={`px-3 py-2 text-right zen-mono font-bold ${d.pct === null ? "text-slate-400" : d.pct < 0 ? "text-rose-500" : d.pct < threshold ? "text-rose-500" : "text-emerald-500"}`}>
                  {d.pct !== null ? `%${d.pct.toFixed(1)}` : "—"}
                </td>
                <td className="px-3 py-2">
                  {d.outOfStock
                    ? <span className="px-1.5 py-0.5 rounded text-[9px] font-black bg-rose-500/15 text-rose-500 border border-rose-500/30">STOK DIŞI</span>
                    : d.pct !== null && d.pct < 0 ? <span className="text-rose-500 font-black text-[10px]">ZARARINA</span> : <span className="text-slate-400">—</span>}
                </td>
              </tr>
            ))}
            {view.length === 0 && <tr><td colSpan={9} className="px-3 py-6 text-center text-slate-500">Kayıt yok</td></tr>}
          </tbody>
        </table>
      </div>
      <div className="text-[10px] text-slate-500">{view.length} satır · Marja göre artan sıralı (en zayıf marj üstte). Alış fiyatları TCMB kuru ile TL'ye çevrilmiştir.</div>
    </div>
  );
}

// ══════════════ ALIŞ FİYAT LİSTELERİ (Tedarikçi Excel Arşivi) ══════════════
export function PurchaseListsModule({ api }) {
  const [lists, setLists] = useState([]);
  const [busy, setBusy] = useState("");
  const [uploader, setUploader] = useState(false);
  const [diffFor, setDiffFor] = useState(null);
  const [brands, setBrands] = useState([]);

  const load = async () => {
    const [l, a] = await Promise.all([api.get("/purchase-lists"), api.get("/assets?kind=fabric")]);
    setLists(l.data);
    setBrands([...new Set(a.data.map((x) => x.brand).filter(Boolean))].sort());
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const remove = async (id) => {
    if (!window.confirm("Bu alış fiyat listesi silinsin mi? (Uygulanmış fiyatlar geri alınmaz)")) return;
    await api.delete(`/purchase-lists/${id}`); toast.success("Liste silindi"); load();
  };

  const download = async (pl) => {
    setBusy(pl.id);
    try {
      const { data } = await api.get(`/purchase-lists/${pl.id}/file`);
      const a = document.createElement("a");
      a.href = `data:application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64,${data.base64}`;
      a.download = data.fileName; a.click();
    } catch { toast.error("Dosya indirilemedi"); } finally { setBusy(""); }
  };

  return (
    <div className="space-y-4" data-testid="purchase-lists-module">
      <div className="flex flex-wrap items-center gap-3">
        <div>
          <h3 className="text-sm font-bold">Alış Fiyat Listeleri</h3>
          <p className="text-[11px] text-slate-500">Her tedarikçinin Excel listesi programda saklanır. Yeni liste geldiğinde farkları tek tek veya toplu uygulayın.</p>
        </div>
        <div className="flex-1" />
        <button onClick={() => setUploader(true)} className="zen-btn-primary" data-testid="pl-new">
          <Upload className="w-4 h-4" /> Yeni Liste Yükle
        </button>
      </div>

      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-3">
        {lists.map((pl) => (
          <div key={pl.id} className="zen-panel p-4 space-y-2" data-testid={`pl-card-${pl.id}`}>
            <div className="flex items-start gap-2">
              <FileSpreadsheet className="w-5 h-5 text-emerald-500 shrink-0 mt-0.5" />
              <div className="min-w-0 flex-1">
                <div className="text-sm font-bold truncate">{pl.name || pl.fileName}</div>
                <div className="text-[10px] text-slate-500 truncate">{pl.supplier || "Tedarikçi belirtilmedi"} · {pl.rowCount} satır · {pl.currency || "TL"} · {(pl.fileSize / 1024 / 1024).toFixed(1)} MB</div>
                <div className="text-[10px] text-slate-400">{new Date(pl.uploadedAt).toLocaleString("tr-TR")}</div>
              </div>
            </div>
            {pl.appliedAt && (
              <div className="text-[10px] text-emerald-600 dark:text-emerald-400 font-semibold flex items-center gap-1">
                <CheckCircle2 className="w-3 h-3" /> {pl.appliedCount} kayıt uygulandı · {new Date(pl.appliedAt).toLocaleDateString("tr-TR")}
              </div>
            )}
            <div className="flex flex-wrap gap-1.5 pt-1">
              <button onClick={() => setDiffFor(pl)} className="zen-btn-secondary !py-1 !px-2 !text-[10px]" data-testid={`pl-diff-${pl.id}`}>
                <RefreshCw className="w-3 h-3" /> Farkları Gör
              </button>
              <button onClick={() => download(pl)} disabled={busy === pl.id} className="zen-btn-secondary !py-1 !px-2 !text-[10px]" data-testid={`pl-download-${pl.id}`}>
                <Download className="w-3 h-3" /> {busy === pl.id ? "…" : "Excel"}
              </button>
              <button onClick={() => remove(pl.id)} className="zen-btn-secondary !py-1 !px-2 !text-[10px] !text-rose-500" data-testid={`pl-delete-${pl.id}`}>
                <Trash2 className="w-3 h-3" /> Sil
              </button>
            </div>
          </div>
        ))}
        {lists.length === 0 && <div className="text-xs text-slate-500 col-span-full py-8 text-center">Henüz alış fiyat listesi yüklenmedi.</div>}
      </div>

      {uploader && <UploadWizard api={api} brands={brands} onClose={() => setUploader(false)} onDone={() => { setUploader(false); load(); }} />}
      {diffFor && <DiffModal api={api} pl={diffFor} onClose={() => setDiffFor(null)} onApplied={() => { setDiffFor(null); load(); }} />}
    </div>
  );
}

function UploadWizard({ api, brands, onClose, onDone }) {
  const [supplier, setSupplier] = useState("Vatan Perde");
  const [currency, setCurrency] = useState("TL");
  const [name, setName] = useState("");
  const [file, setFile] = useState(null);
  const [step, setStep] = useState("pick");   // pick | uploading | map | done
  const [progress, setProgress] = useState(0);
  const [pid, setPid] = useState(null);
  const [sheets, setSheets] = useState([]);
  const [isVatan, setIsVatan] = useState(false);
  const [map, setMap] = useState({ sheet: "", headerRow: 1, codeCol: 0, labelCol: 1, priceCol: 3, stockCol: "" });

  const start = async () => {
    if (!file) return toast.error("Excel dosyası seçin");
    setStep("uploading");
    try {
      const { data } = await api.post("/purchase-lists", {
        supplier, currency, name: name || file.name.replace(/\.xlsx?$/i, ""), fileName: file.name, fileSize: file.size,
      });
      setPid(data.id);
      const b64 = await new Promise((res, rej) => {
        const fr = new FileReader();
        fr.onload = () => res(String(fr.result).split(",")[1]);
        fr.onerror = rej; fr.readAsDataURL(file);
      });
      const total = Math.ceil(b64.length / CHUNK);
      for (let i = 0; i < total; i++) {
        await api.post(`/purchase-lists/${data.id}/chunk`, { index: i, total, data: b64.slice(i * CHUNK, (i + 1) * CHUNK) });
        setProgress(Math.round(((i + 1) / total) * 100));
      }
      const s = await api.get(`/purchase-lists/${data.id}/sheets`);
      setSheets(s.data.sheets); setIsVatan(s.data.isVatan);
      setMap((m) => ({ ...m, sheet: s.data.sheets[0]?.name || "" }));
      if (s.data.isVatan) {
        const p = await api.post(`/purchase-lists/${data.id}/parse`, { format: "vatan" });
        toast.success(`Vatan Perde formatı tanındı — ${p.data.rowCount} fiyat satırı okundu`);
        onDone();
      } else setStep("map");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Yükleme başarısız");
      setStep("pick");
    }
  };

  const parseCustom = async () => {
    try {
      const p = await api.post(`/purchase-lists/${pid}/parse`, { format: "custom", mapping: map });
      toast.success(`${p.data.rowCount} fiyat satırı okundu`);
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Okuma başarısız"); }
  };

  const preview = sheets.find((s) => s.name === map.sheet);

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" data-testid="pl-upload-modal">
      <div className="zen-panel w-full max-w-3xl max-h-[90vh] overflow-y-auto p-5 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-base font-bold">Alış Fiyat Listesi Yükle</h3>
          <button onClick={onClose} data-testid="pl-upload-close"><X className="w-4 h-4" /></button>
        </div>

        {step === "pick" && (
          <div className="space-y-3">
            <div className="grid sm:grid-cols-2 gap-3">
              <div><label className="block text-xs font-semibold mb-1">Tedarikçi / Firma</label>
                <input className="zen-input" list="pl-brands" value={supplier} onChange={(e) => setSupplier(e.target.value)} data-testid="pl-supplier" />
                <datalist id="pl-brands">{brands.map((b) => <option key={b} value={b} />)}</datalist>
              </div>
              <div><label className="block text-xs font-semibold mb-1">Liste Adı</label>
                <input className="zen-input" placeholder="örn. Özel Mayıs 2026" value={name} onChange={(e) => setName(e.target.value)} data-testid="pl-name" />
              </div>
            </div>
            <div><label className="block text-xs font-semibold mb-1">Listedeki Fiyatların Para Birimi</label>
              <select className="zen-input !w-auto" value={currency} onChange={(e) => setCurrency(e.target.value)} data-testid="pl-currency">
                <option>TL</option><option>EUR</option><option>USD</option>
              </select>
            </div>
            <div><label className="block text-xs font-semibold mb-1">Excel Dosyası (.xlsx)</label>
              <input type="file" accept=".xlsx,.xlsm" className="zen-input !py-2" onChange={(e) => setFile(e.target.files[0])} data-testid="pl-file" />
              {file && <div className="text-[10px] text-slate-500 mt-1">{file.name} · {(file.size / 1024 / 1024).toFixed(1)} MB</div>}
            </div>
            <div className="text-[11px] text-slate-500">Vatan Perde formatı otomatik tanınır. Diğer firmalarda sütun eşleme ekranı açılır.</div>
            <div className="flex gap-2 justify-end">
              <button onClick={onClose} className="zen-btn-secondary">İptal</button>
              <button onClick={start} className="zen-btn-primary" data-testid="pl-upload-start"><Upload className="w-4 h-4" /> Yükle ve Oku</button>
            </div>
          </div>
        )}

        {step === "uploading" && (
          <div className="py-8 space-y-3 text-center">
            <div className="text-sm font-semibold">Dosya yükleniyor… %{progress}</div>
            <div className="h-2 bg-slate-200 dark:bg-[#22304A] rounded-full overflow-hidden">
              <div className="h-full bg-[#E5322A] transition-all" style={{ width: `${progress}%` }} />
            </div>
            <div className="text-[11px] text-slate-500">Büyük dosyalar parça parça yükleniyor, sayfayı kapatmayın.</div>
          </div>
        )}

        {step === "map" && (
          <div className="space-y-3" data-testid="pl-mapping">
            <div className="text-[11px] text-amber-600 dark:text-amber-400 font-semibold">
              {isVatan ? "Vatan formatı" : "Bilinmeyen format"} — hangi sütunun ne olduğunu seçin.
            </div>
            <div className="grid sm:grid-cols-3 gap-2">
              <div><label className="block text-[10px] font-bold mb-1">Sayfa</label>
                <select className="zen-input !py-1.5" value={map.sheet} onChange={(e) => setMap({ ...map, sheet: e.target.value })} data-testid="pl-map-sheet">
                  {sheets.map((s) => <option key={s.name} value={s.name}>{s.name} ({s.totalRows} satır)</option>)}
                </select>
              </div>
              <div><label className="block text-[10px] font-bold mb-1">Başlık satırı</label>
                <input type="number" min={0} className="zen-input !py-1.5" value={map.headerRow} onChange={(e) => setMap({ ...map, headerRow: Number(e.target.value) })} data-testid="pl-map-header" />
              </div>
              <div />
              {[["codeCol", "Kod sütunu"], ["labelCol", "Ürün adı sütunu"], ["priceCol", "Fiyat sütunu"], ["stockCol", "Stok durumu sütunu (ops.)"]].map(([k, lbl]) => (
                <div key={k}><label className="block text-[10px] font-bold mb-1">{lbl}</label>
                  <select className="zen-input !py-1.5" value={map[k]} onChange={(e) => setMap({ ...map, [k]: e.target.value === "" ? "" : Number(e.target.value) })} data-testid={`pl-map-${k}`}>
                    {k === "stockCol" && <option value="">— yok —</option>}
                    {(preview?.rows[0] || []).map((_, i) => <option key={i} value={i}>{String.fromCharCode(65 + i)} sütunu</option>)}
                  </select>
                </div>
              ))}
            </div>
            {preview && (
              <div className="overflow-x-auto border border-slate-200 dark:border-[#22304A] rounded-lg">
                <table className="text-[10px] w-full">
                  <tbody>
                    {preview.rows.map((r, i) => (
                      <tr key={i} className={i + 1 === map.headerRow ? "bg-amber-500/10" : ""}>
                        <td className="px-2 py-1 text-slate-400 zen-mono">{i + 1}</td>
                        {r.map((c, j) => (
                          <td key={j} className={`px-2 py-1 truncate max-w-32 ${[map.codeCol, map.priceCol].includes(j) ? "bg-[#E5322A]/10 font-semibold" : ""}`}>{c}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <div className="flex gap-2 justify-end">
              <button onClick={onClose} className="zen-btn-secondary">İptal</button>
              <button onClick={parseCustom} className="zen-btn-primary" data-testid="pl-map-parse">Listeyi Oku</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function DiffModal({ api, pl, onClose, onApplied }) {
  const [diff, setDiff] = useState(null);
  const [sel, setSel] = useState({});
  const [applying, setApplying] = useState(false);
  const [showUnmatched, setShowUnmatched] = useState(false);

  useEffect(() => {
    const esc = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, [onClose]);

  useEffect(() => {
    api.get(`/purchase-lists/${pl.id}/diff`).then((r) => {
      setDiff(r.data);
      setSel(Object.fromEntries(r.data.changed.map((c) => [c.code, true])));
    }).catch(() => toast.error("Fark listesi alınamadı"));
  }, [pl.id]); // eslint-disable-line

  const apply = async (all) => {
    const codes = all ? null : Object.keys(sel).filter((k) => sel[k]);
    if (!all && codes.length === 0) return toast.error("Hiç satır seçilmedi");
    setApplying(true);
    try {
      const { data } = await api.post(`/purchase-lists/${pl.id}/apply`, all ? {} : { codes });
      toast.success(`${data.applied} kayıt güncellendi`);
      onApplied();
    } catch { toast.error("Uygulama başarısız"); } finally { setApplying(false); }
  };

  const changed = diff?.changed || [];
  const selCount = Object.values(sel).filter(Boolean).length;

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" data-testid="pl-diff-modal">
      <div className="zen-panel w-full max-w-5xl max-h-[90vh] flex flex-col p-5">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="text-base font-bold">{pl.name || pl.fileName} — Fiyat Farkları</h3>
            <div className="text-[11px] text-slate-500">
              {diff ? `${changed.length} değişiklik · ${diff.sameCount} aynı · ${diff.unmatched.length} eşleşmeyen · toplam ${diff.rowCount} satır` : "Hesaplanıyor…"}
            </div>
          </div>
          <button onClick={onClose} data-testid="pl-diff-close"><X className="w-4 h-4" /></button>
        </div>

        <div className="flex-1 overflow-y-auto">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 dark:bg-[#131B2E] text-slate-500 sticky top-0">
              <tr className="text-left">
                <th className="px-2 py-2">
                  <input type="checkbox" data-testid="pl-diff-select-all"
                    checked={changed.length > 0 && selCount === changed.length}
                    onChange={(e) => setSel(Object.fromEntries(changed.map((c) => [c.code, e.target.checked])))} />
                </th>
                <th className="px-2 py-2 font-bold">Kumaş</th>
                <th className="px-2 py-2 font-bold">Kod</th>
                <th className="px-2 py-2 font-bold text-right">Eski Alış</th>
                <th className="px-2 py-2 font-bold text-right">Yeni Alış</th>
                <th className="px-2 py-2 font-bold text-right">Değişim</th>
                <th className="px-2 py-2 font-bold">Stok</th>
              </tr>
            </thead>
            <tbody>
              {changed.map((c) => (
                <tr key={c.code} className="border-t border-slate-100 dark:border-[#22304A]" data-testid={`pl-diff-row-${c.code}`}>
                  <td className="px-2 py-1.5">
                    <input type="checkbox" checked={!!sel[c.code]} onChange={(e) => setSel({ ...sel, [c.code]: e.target.checked })} data-testid={`pl-diff-check-${c.code}`} />
                  </td>
                  <td className="px-2 py-1.5 font-semibold">{c.assetLabel || c.label}<div className="text-[10px] text-slate-500">{c.sheet}</div></td>
                  <td className="px-2 py-1.5 zen-mono">{c.code}</td>
                  <td className="px-2 py-1.5 text-right zen-mono text-slate-500">{c.oldPrice ? fmtTL(c.oldPrice) : "—"}</td>
                  <td className="px-2 py-1.5 text-right zen-mono font-bold">{fmtTL(c.price)}</td>
                  <td className={`px-2 py-1.5 text-right zen-mono font-bold ${c.pct === null ? "text-slate-400" : c.pct > 0 ? "text-rose-500" : "text-emerald-500"}`}>
                    {c.pct === null ? "yeni" : `${c.pct > 0 ? "+" : ""}%${c.pct}`}
                  </td>
                  <td className="px-2 py-1.5">
                    {c.outOfStock !== c.oldOutOfStock
                      ? <span className={`px-1.5 py-0.5 rounded text-[9px] font-black ${c.outOfStock ? "bg-rose-500/15 text-rose-500" : "bg-emerald-500/15 text-emerald-500"}`}>{c.outOfStock ? "→ STOK DIŞI" : "→ STOKTA"}</span>
                      : <span className="text-slate-400">—</span>}
                  </td>
                </tr>
              ))}
              {diff && changed.length === 0 && <tr><td colSpan={7} className="px-3 py-6 text-center text-slate-500">Fiyat/stok değişikliği yok — liste güncel.</td></tr>}
            </tbody>
          </table>

          {diff && diff.unmatched.length > 0 && (
            <div className="mt-3">
              <button onClick={() => setShowUnmatched((v) => !v)} className="text-[11px] font-bold text-amber-600 dark:text-amber-400 flex items-center gap-1" data-testid="pl-diff-unmatched-toggle">
                <AlertTriangle className="w-3.5 h-3.5" /> {diff.unmatched.length} satır programdaki kumaşlarla eşleşmedi {showUnmatched ? "▲" : "▼"}
              </button>
              {showUnmatched && (
                <div className="mt-2 text-[10px] text-slate-500 max-h-40 overflow-y-auto space-y-0.5">
                  {diff.unmatched.map((u, i) => <div key={i}>{u.sheet} · <b>{u.code}</b> {u.label} → {fmtTL(u.price)}</div>)}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex flex-wrap gap-2 justify-end pt-3 border-t border-slate-200 dark:border-[#22304A] mt-3">
          <button onClick={onClose} className="zen-btn-secondary">Kapat</button>
          <button onClick={() => apply(false)} disabled={applying || selCount === 0} className="zen-btn-secondary" data-testid="pl-apply-selected">
            Seçilenleri Uygula ({selCount})
          </button>
          <button onClick={() => apply(true)} disabled={applying} className="zen-btn-primary" data-testid="pl-apply-all">
            <CheckCircle2 className="w-4 h-4" /> {applying ? "Uygulanıyor…" : "Tümünü Uygula"}
          </button>
        </div>
      </div>
    </div>
  );
}
